import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';

const STATUS_STYLE = {
  done:     { dot: 'var(--acc)', text: 'text-[var(--ink-soft)]', ring: false },
  current:  { dot: 'var(--acc)', text: 'text-[var(--ink)] font-semibold', ring: true },
  upcoming: { dot: 'var(--panel-2)', text: 'text-[var(--ink-soft)]', ring: false },
};

const MILESTONE_STATUS_LABEL = {
  pending: 'Not scheduled', scheduled: 'Scheduled', in_progress: 'In progress', complete: 'Done', skipped: 'Skipped',
};

function AccessChecklistItem({ item, onToggle }) {
  const [showGuide, setShowGuide] = useState(false);
  const shared = item.status === 'shared';
  return (
    <li className="p-2 border-b border-[var(--line)] last:border-b-0">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={shared}
          onChange={(e) => item.key && onToggle.mutate({ key: item.key, shared: e.target.checked })}
          disabled={!item.key}
          className="mt-1"
          style={{ width: 16, height: 16, accentColor: 'var(--acc)', flexShrink: 0 }}
        />
        <span className="flex-1 min-w-0">
          <span className="text-sm" style={{ color: shared ? 'var(--ink-soft)' : 'var(--ink)', textDecoration: shared ? 'line-through' : 'none' }}>
            {item.title}
          </span>
          <span className="block text-xs text-[var(--ink-soft)]">{item.how}</span>
          {item.guide && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowGuide((v) => !v); }}
              className="mono mt-1"
              style={{ fontSize: 11, color: 'var(--acc-ink)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              {showGuide ? 'Hide steps' : 'How do I do this?'}
            </button>
          )}
          {showGuide && item.guide && (
            <span className="block mt-1 text-xs" style={{ color: 'var(--ink-soft)', lineHeight: 1.5, background: 'var(--acc-soft)', borderRadius: 8, padding: '8px 10px' }}>
              {item.guide}
            </span>
          )}
        </span>
      </label>
    </li>
  );
}

export default function Project() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['lifecycle'],
    queryFn: () => apiFetch('/api/console/lifecycle'),
  });

  const confirmAccess = useMutation({
    mutationFn: () => apiFetch('/api/console/lifecycle/confirm-access', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifecycle'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  const toggleItem = useMutation({
    mutationFn: ({ key, shared }) =>
      apiFetch('/api/console/lifecycle/access-items', { method: 'POST', body: { key, shared } }),
    // Optimistic: flip the cached item immediately so the checkbox feels instant.
    onMutate: async ({ key, shared }) => {
      await queryClient.cancelQueries({ queryKey: ['lifecycle'] });
      const prev = queryClient.getQueryData(['lifecycle']);
      queryClient.setQueryData(['lifecycle'], (old) =>
        old ? { ...old, access_items: (old.access_items || []).map((it) => it.key === key ? { ...it, status: shared ? 'shared' : 'pending' } : it) } : old);
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) queryClient.setQueryData(['lifecycle'], ctx.prev); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['lifecycle'] }),
  });

  return (
    <div className="px-4 pt-6 pb-4">
      <header className="mb-5">
        <h1 className="d-xl text-2xl">Your Project</h1>
        <p className="serif-i text-base text-[var(--ink-soft)]">Where things stand and what's next.</p>
      </header>

      {isLoading && <p className="text-sm text-[var(--ink-soft)]">Loading…</p>}
      {error && <p className="text-sm text-[var(--bad)]">Couldn't load your project. Please try again in a moment.</p>}

      {data && (
        <div className="space-y-6">
          {/* Stepper */}
          <section>
            <h2 className="mlabel mb-2">Progress</h2>
            <ol className="relative ml-1 border-l border-[var(--line)] pl-5">
              {(data.stages || []).map((s) => {
                const st = STATUS_STYLE[s.status] || STATUS_STYLE.upcoming;
                return (
                  <li key={s.key} className="relative mb-4 last:mb-0">
                    <span
                      className="absolute -left-[27px] top-1 h-3 w-3 rounded-full"
                      style={{
                        background: st.dot,
                        boxShadow: st.ring ? '0 0 0 4px var(--acc-soft)' : 'none',
                      }}
                    />
                    <span className={`text-sm ${st.text}`}>{s.label}</span>
                    {s.status === 'current' && (
                      <span className="stamp ml-2" style={{ color: 'var(--acc-ink)' }}>You are here</span>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>

          {/* Access confirmation — self-report so the stage advances without waiting on us */}
          {data.stage === 'access_pending' && (
            <section>
              <button
                type="button"
                onClick={() => confirmAccess.mutate()}
                disabled={confirmAccess.isPending}
                className="btnx primary w-full"
              >
                {confirmAccess.isPending ? 'Confirming…' : "I've shared access"}
              </button>
              {confirmAccess.isError && (
                <p className="mt-2 text-xs text-[var(--bad)]">Couldn't confirm — please try again.</p>
              )}
            </section>
          )}

          {/* Kickoff CTA — books through our own meetings flow (Google Meet link included) */}
          {data.stage === 'kickoff' && (
            <section>
              <button
                type="button"
                onClick={() => navigate('/meetings')}
                className="btnx primary w-full"
              >
                Book your kickoff call
              </button>
            </section>
          )}

          {/* Meetings / milestones */}
          {Array.isArray(data.milestones) && data.milestones.length > 0 && (
            <section>
              <h2 className="mlabel mb-2">Meetings</h2>
              <ul className="divide-y divide-[var(--line)] panel">
                <i className="brk brk-tl" /><i className="brk brk-br" />
                {data.milestones.map((m) => (
                  <li key={m.id || m.milestone_key} className="flex items-center justify-between p-3">
                    <span className="text-sm">{m.label}</span>
                    <span className="mono text-xs text-[var(--ink-soft)]">
                      {MILESTONE_STATUS_LABEL[m.status] || m.status}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* What we need from you — a resumable checklist. Tick each grant as
              you make it; hard items have a step-by-step guide. */}
          {Array.isArray(data.access_items) && data.access_items.length > 0 && (
            <section>
              {(() => {
                const done = data.access_items.filter((it) => it.status === 'shared').length;
                const total = data.access_items.length;
                return (
                  <div className="mb-2 flex items-baseline justify-between">
                    <h2 className="mlabel">What we need from you</h2>
                    <span className="mono text-xs text-[var(--ink-soft)]">{done} of {total} shared</span>
                  </div>
                );
              })()}
              <ul className="panel p-2">
                <i className="brk brk-tl" /><i className="brk brk-br" />
                {data.access_items.map((it) => (
                  <AccessChecklistItem key={it.key || it.title} item={it} onToggle={toggleItem} />
                ))}
              </ul>
            </section>
          )}

          {/* Your files */}
          {data.drive_folder_url && (
            <section>
              <h2 className="mlabel mb-2">Your files</h2>
              <a href={data.drive_folder_url} target="_blank" rel="noopener noreferrer"
                 className="lrow hoverable text-sm text-[var(--ink)]">
                Open your Drive folder ↗
              </a>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
